// components/auth/login-hero-panel.tsx
// The left side of the redesigned sign-in page (hidden below md, where the
// login card alone is plenty). A real aerial photo of Alwasail's own
// pipe-manufacturing facility (public/images/alwasail.jpeg), provided by
// the engineer to replace the earlier gradient-only version. A
// bottom-anchored dark scrim (brand primary-800/secondary-700 tones, not
// flat black) keeps the overlaid badge/headline/subtext legible. No logo
// here: CardWrapper already shows the Alwasail logo on the card side, so
// a second one here just doubled up.
import {
  isNationalDayWindowActive,
  pickNationalDayBannerImage,
} from "@/components/auth/national-day-theme";
import { LanguageType } from "@/lib/types";
import { _t, cn } from "@/lib/utils";
import Image from "next/image";

export function LoginHeroPanel({
  language,
  className,
}: {
  language: LanguageType;
  className?: string;
}) {
  // Saudi National Day theming (time-boxed, see national-day-theme.tsx):
  // a greener scrim, a small flag badge, and (live feedback, 2026-09-22:
  // "with big images... let's see if will work") the real GEA campaign
  // photography swapped in for the usual facility photo, for a few days
  // around 23/09.
  const isNationalDay = isNationalDayWindowActive();
  const heroImage = isNationalDay ? pickNationalDayBannerImage("login-hero") : "/images/alwasail.jpeg";

  return (
    <div
      className={cn(
        // overflow-hidden here is scoped to this panel alone (clips the
        // background photo's own hover zoom to its own box), not a shared
        // ancestor wrapper -- nothing else on the page can be cropped by it.
        "relative hidden md:flex md:flex-1 flex-col justify-end overflow-hidden rounded-s-3xl p-10 text-white",
        className,
      )}
    >
      <Image
        src={heroImage}
        alt=""
        fill
        priority
        sizes="(min-width: 768px) 50vw, 0px"
        className="object-cover scale-100 group-hover:scale-110 transition-transform duration-700 ease-out"
        style={isNationalDay ? { objectPosition: "50% 25%" } : undefined}
      />
      <div
        className="absolute inset-0"
        style={
          isNationalDay
            ? {
                background:
                  "linear-gradient(180deg, rgba(6,54,38,0.20) 0%, rgba(6,54,38,0.45) 55%, rgba(6,44,31,0.90) 100%)",
              }
            : {
                background:
                  "linear-gradient(180deg, rgba(27,49,55,0.15) 0%, rgba(27,49,55,0.35) 55%, rgba(27,49,55,0.88) 100%)",
              }
        }
      />

      {isNationalDay && (
        <span className="absolute top-6 start-6 inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-xs font-medium tracking-wide text-amber-100">
          🇸🇦 {_t("national_day_badge", language)}
        </span>
      )}

      <div className="relative space-y-5">
        <span className="inline-flex items-center rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs font-medium tracking-wide">
          {_t("login_hero_badge", language)}
        </span>
        <h1
          className="text-4xl leading-tight font-semibold"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {_t("login_hero_headline", language)}
        </h1>
        <p className="text-white/85 text-base leading-relaxed max-w-sm">
          {_t("login_hero_subtext", language)}
        </p>
      </div>
    </div>
  );
}
